import React, { useId, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import type { Tool } from '../../types/tools';
import { SectionHeading } from '../ui/SectionHeading';
import { cn } from '../../src/lib/utils';

/** Accessible FAQ accordion. Answers stay in the DOM for crawlers and find-in-page. */
export function ToolFaq({
  tool
}: {
  tool: Tool;
}) {
  const [open, setOpen] = useState<number | null>(0);
  const baseId = useId();
  return <section aria-labelledby="faq-heading" className="border-b border-hairline-soft bg-ink" data-id="element-1130" data-mp="4lpb64d8ecuw">
      <div className="mx-auto w-full max-w-[900px] px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-id="element-1131" data-mp="3vtbkacipz7j5">
        <SectionHeading eyebrow="FAQ" headingId="faq-heading" title={`${tool.name} questions`} data-id="element-1132" data-mp="ec1vbvh4w40i" />
        <ul className="mt-10 divide-y divide-hairline rounded-2xl border border-hairline bg-panel" data-id="element-1133" data-mp="9fl42sen19j7">
          {tool.faqs.map((faq, index) => {
          const expanded = open === index;
          const buttonId = `${baseId}-q-${index}`;
          const panelId = `${baseId}-a-${index}`;
          return <li key={faq.question} data-id="element-1134" data-mp="37oovr43az858" data-mpi={index}>
                <h3 data-id="element-1135" data-mp="3clp2zibw2gxx">
                  <button id={buttonId} type="button" aria-expanded={expanded} aria-controls={panelId} onClick={() => setOpen(expanded ? null : index)} className="flex min-h-[56px] w-full items-center justify-between gap-4 px-5 py-4 text-left text-[15px] font-semibold text-text-primary transition-colors hover:text-lime focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime" data-id="element-1136" data-mp="3cl5l21m8nsjw">
                    {faq.question}
                    <ChevronDownIcon className={cn('h-4 w-4 shrink-0 text-text-tertiary transition-transform duration-200 motion-reduce:transition-none', expanded && 'rotate-180')} aria-hidden="true" data-id="element-1137" data-mp="95hyxapkahom" />
                  </button>
                </h3>
                <div id={panelId} role="region" aria-labelledby={buttonId} hidden={!expanded} className="px-5 pb-5 text-sm leading-relaxed text-text-secondary" data-id="element-1138" data-mp="2a7pwke4e1a87">
                  {faq.answer}
                </div>
              </li>;
        })}
        </ul>
        <p className="mt-6 text-xs text-text-tertiary" data-id="element-1139" data-mp="2xyva2mmuvqfc">
          For data handling details, see our{' '}
          <a href="https://www.dgpt.io/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-text-primary" data-id="element-1140" data-mp="36xqh20xq54jp">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="https://www.dgpt.io/terms" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-text-primary" data-id="element-1141" data-mp="1cj5zhx4p9p4j">
            Terms
          </a>
          .
        </p>
      </div>
    </section>;
}